<script setup>
import bg from '@/assets/img/case/case-detail_bg.svg'
import { Swiper, SwiperSlide } from 'swiper/vue'
import { Navigation } from 'swiper/modules'
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from 'vue'
import { useRoute, useRouter } from 'vue-router'

const props = defineProps({
	items: {
		type: Object,
		required: true,
		default: () => ({ categories: [], cases: [] }),
	},
})

const emit = defineEmits(['category-click'])
const route = useRoute()
const router = useRouter()

// --- State ---
const activeCategorySlug = ref(route.query.category || null)
const selectedCaseId = ref(route.query.id || null)
const swiperInstance = ref(null)
const navigationRef = ref(null)
const actionTop = ref('0rem')
const hoveredIndex = ref(null)
const randomHoverIndex = ref(null)

// --- Computed ---
const showCaseDetail = computed(() => !!route.query.id)
const filteredCases = computed(() => props.items?.cases || [])
const initialSlideIndex = computed(() =>
	filteredCases.value.findIndex((item) => item.id == selectedCaseId.value),
)

// --- Functions ---
const addLineBreaks = (text) => {
	if (!text) return ''
	let formatted = text.replace(/\\n/g, '<br>').replace(/\n/g, '<br>')
	formatted = formatted.replace(/&lt;br\s*\/?&gt;/g, '<br>').trim()
	return formatted
}

const onSwiper = (swiper) => {
	swiperInstance.value = swiper
}

function setActiveCategory(slug) {
	activeCategorySlug.value = slug
	router.replace({
		query: { ...route.query, category: slug, id: undefined },
	})
	emit('category-click', slug)
}

// --- Action Top Calculation ---
const calculateActionTop = async () => {
	await nextTick()
	if (navigationRef.value) {
		const navHeightPx = navigationRef.value.getBoundingClientRect().height
		const navHeightRem = navHeightPx / 10 // 1rem = 10px
		actionTop.value = `${navHeightRem + 30}rem`
	}
}

// --- Watches ---
watch(
	[() => props.items.categories, () => props.items.cases],
	async () => {
		await nextTick()
		calculateActionTop()
	},
	{ immediate: true },
)

// Инициализация слайда при загрузке кейсов
// --- Watch filteredCases и selectedCaseId ---
watch(
	[filteredCases, selectedCaseId, () => swiperInstance.value],
	async ([cases, id, swiper]) => {
		if (!swiper || !cases.length || !id) return
		await nextTick()
		const index = cases.findIndex((item) => item.id == id)
		if (index !== -1) {
			swiper.slideTo(index, 0) // сразу показываем выбранный кейс
		}
	},
	{ immediate: true },
)

// --- При клике на кейс из списка ---
function openCase(itemId) {
	router.replace({
		query: { ...route.query, id: itemId },
	})
	selectedCaseId.value = itemId
	// swiper.slideTo будет выполнен через watch выше
}

onMounted(async () => {
	randomHoverIndex.value = Math.floor(Math.random() * 6) + 1
})

onMounted(() => {
	calculateActionTop()
	window.addEventListener('resize', calculateActionTop)
})

onBeforeUnmount(() => {
	window.removeEventListener('resize', calculateActionTop)
})
</script>

<template>
	<section class="case-detail">
		<div
			class="case-detail__bg"
			v-parallax
			:data-prlx-cx="1000"
			:data-prlx-cy="1000"
			:data-prlx-a="50"
		>
			<img :src="bg" alt="" />
		</div>

		<div class="case-detail__container">
			<div class="case-detail__block">
				<div class="section-header">
					<h2>Кейсы</h2>

					<nav class="case-detail__navigation" ref="navigationRef">
						<button
							v-for="tab in items.categories"
							:key="tab.slug"
							type="button"
							:class="[
								'case-detail__title',
								{ '_tab-active': activeCategorySlug === tab.slug },
							]"
							@click="setActiveCategory(tab.slug)"
						>
							{{ tab.name }}
						</button>
					</nav>
				</div>

				<div class="case-detail__content" v-if="!showCaseDetail">
					<div class="case__wrapper">
						<div
							:class="[
								'case__list',
								hoveredIndex !== null
									? `hover-${hoveredIndex + 1}`
									: `hover-${randomHoverIndex}`,
							]"
						>
							<div
								v-for="(item, index) in filteredCases"
								:key="item.id"
								:class="[
									'case__item',
									{ 'case__item--animated': caseAnimationStarted },
								]"
								:style="{
									transitionDelay: caseAnimationStarted
										? `${index * 50}ms`
										: '0ms',
								}"
								@mouseenter="hoveredIndex = index"
								@mouseleave="hoveredIndex = null"
							>
								<router-link
									class="case__link"
									:to="{
										name: 'Cases',
										query: {
											category: activeCategorySlug,
											id: item.id,
										},
									}"
									@click.prevent="openCase(item.id)"
								>
									<div class="case__img">
										<img :src="item.image_main" :alt="item.title" />
									</div>
									<div class="case__desc">
										<h4 class="dont-break-out">{{ item.title }}</h4>
										<h5>{{ item.awards }}</h5>
									</div>
								</router-link>
							</div>
						</div>
					</div>
				</div>

				<div class="case-detail__content" v-else>
					<div class="case-detail__wrapper">
						<div class="case-detail__content">
							<Swiper
								class="case-detail__slider"
								:modules="[Navigation]"
								:slides-per-view="1"
								:space-between="20"
								:autoHeight="true"
								:navigation="{
									prevEl: '.case-detail__button-prev',
									nextEl: '.case-detail__button-next',
								}"
								@swiper="onSwiper"
							>
								<SwiperSlide
									v-for="item in filteredCases"
									:key="item.id"
									class="case-detail__slide"
								>
									<div class="case-detail__item">
										<div class="case-detail__img">
											<img :src="item.image_detail" :alt="item.title" />

											<div class="case-detail__logo" v-if="item.image_logo">
												<img :src="item.image_logo" :alt="item.title" />
											</div>
										</div>

										<div class="case-detail__desc">
											<div class="case-detail__part" v-if="item.client">
												<h4>Клиент</h4>
												<h6 v-html="addLineBreaks(item.client)"></h6>
											</div>

											<div class="case-detail__part" v-if="item.task">
												<h4>Задача</h4>
												<h6 v-html="addLineBreaks(item.task)"></h6>
											</div>

											<div class="case-detail__part" v-if="item.realized">
												<h4>Реализовано</h4>
												<h6 v-html="addLineBreaks(item.realized)"></h6>
											</div>

											<div class="case-detail__part" v-if="item.awards">
												<h4>Награды</h4>
												<h6 v-html="addLineBreaks(item.awards)"></h6>
											</div>

											<div
												class="case-detail__list"
												v-if="item.participants && item.kpi"
											>
												<ul>
													<li v-if="item.participants">
														<h3>{{ item.participants }}</h3>
														<h6>Участников</h6>
													</li>
													<li v-if="item.kpi">
														<h3>{{ item.kpi }}</h3>
														<h6>KPI</h6>
													</li>
												</ul>
											</div>
										</div>
									</div>
								</SwiperSlide>
							</Swiper>
						</div>
					</div>

					<div class="case-detail__action" :style="{ top: actionTop }">
						<div class="case-detail__buttons">
							<button class="arrow-btn case-detail__button-prev" type="button">
								<div class="arrow-btn__icon">
									<svg
										viewBox="0 0 100 100"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M62.8328 83.5L28.5 49.5074L61.8379 16.5L71.7657 26.3294L48.0956 49.7655L72.5 73.9287L62.8328 83.5Z"
											fill="#F27699"
										/>
									</svg>
								</div>

								<svg
									class="arrow-btn__shadow"
									preserveAspectRatio="none"
									viewBox="0 0 130 126"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path d="M10 126L0 116.308H120L130 126H10Z" fill="#818B8F" />
									<path
										d="M130 9.70376L120 0L120 116.308L130 126L130 9.70376Z"
										fill="#3A3E40"
									/>
								</svg>
							</button>

							<button class="arrow-btn case-detail__button-next" type="button">
								<div class="arrow-btn__icon">
									<svg
										viewBox="0 0 100 100"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M38.1671 16.5L72.5 50.4926L39.1621 83.5L29.2344 73.6706L52.9044 50.2345L28.5 26.0713L38.1671 16.5Z"
											fill="#F27699"
										/>
									</svg>
								</div>

								<svg
									class="arrow-btn__shadow"
									preserveAspectRatio="none"
									viewBox="0 0 130 126"
									fill="none"
									xmlns="http://www.w3.org/2000/svg"
								>
									<path d="M10 126L0 116.308H120L130 126H10Z" fill="#818B8F" />
									<path
										d="M130 9.70376L120 0L120 116.308L130 126L130 9.70376Z"
										fill="#3A3E40"
									/>
								</svg>
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</section>
</template>

<style scoped lang="scss">
.case-detail {
	padding: 4.9rem 0 14.9rem;
	position: relative;

	@media (min-width: 991.98px) {
		.section-header {
			display: flex;
			align-items: center;
			justify-content: space-between;
		}
	}

	@media (max-width: 991.98px) {
		padding-bottom: 5rem;
	}

	// .case__bg
	&__bg {
		position: absolute;
		width: 100%;
		height: 100%;
		top: 0;
		left: 0;
		z-index: -1;

		@media (max-width: 991.98px) {
			display: none;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	// .case__container
	&__container {
		height: 100%;
	}

	&__block {
		position: relative;
		height: 100%;
	}

	// .case__navigation
	&__navigation {
		display: flex;
		gap: 1.2rem;

		@media (min-width: 991.98px) {
			margin-right: 14.7rem;
		}

		@media (max-width: 991.98px) {
			gap: 0.8rem;
			flex-wrap: wrap;
			margin-top: 2rem;
		}
	}

	&__title {
		padding: 1.9rem 2.3rem;
		font-weight: 300;
		font-size: 1.8rem;
		// line-height: 3.2rem;
		line-height: 108%;
		text-transform: uppercase;
		background-color: #2b2e2f;
		transition: background-color 0.3s ease 0s;

		@media (max-width: 991.98px) {
			font-size: 1.6rem;
			padding: 1.1rem 1.4rem;
			white-space: nowrap;
			flex: 1 1 calc(33.33333% - 3rem);
		}

		&._tab-active {
			background-color: #f06793;
		}

		@media (any-hover: hover) {
			&:hover {
				background-color: #f06793;
			}
		}
	}

	// .case-detail__wrapper
	&__wrapper {
		margin-top: 9rem;

		@media (max-width: 991.98px) {
			margin-top: 3rem;
		}
	}

	// .case-detail__slider
	&__slider {
	}

	// .case-detail__slide
	&__slide {
		// min-height: 1px;
		// height: auto;
	}

	// .case-detail__item
	&__item {
		display: flex;
		gap: 6rem;
		width: 100%;
		height: 100%;

		@media (max-width: 991.98px) {
			flex-direction: column;
			gap: 2rem;
		}
	}

	// .case-detail__img
	&__img {
		width: 100%;
		height: 100%;
		max-width: 108.2rem;
		max-height: 63rem;
		padding-left: 6rem;

		@media (max-width: 991.98px) {
			padding: 0;
			max-height: 18.7rem;
			max-width: 100%;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
			object-position: top left;
			max-height: 63rem;

			@media (max-width: 991.98px) {
				max-height: 18.7rem;
			}
		}
	}

	// .case-detail__logo
	&__logo {
		position: absolute;
		top: 0;
		left: 0;
		width: 12.4rem;
		height: 12.4rem;
		font-size: 1.6rem;
		display: flex;
		align-items: flex-end;

		@media (max-width: 991.98px) {
			width: 3.7rem;
			height: 3.7rem;
		}

		img {
			width: 100%;
			height: 100%;
			object-fit: contain;
		}
	}

	// .case-detail__desc
	&__desc {
		width: 100%;
		max-width: 51.5rem;

		@media (max-width: 991.98px) {
			margin-top: 12.4rem;
		}

		h3 {
			font-weight: 700;
			font-size: 6rem;
			// line-height: 6.5rem;
			line-height: 108%;
			text-transform: uppercase;
			color: #f27699;

			@media (max-width: 991.98px) {
				font-size: 3rem;
			}
		}

		h4 {
			font-weight: 500;
			font-size: 2.4rem;
			// line-height: 3.4rem;
			line-height: 140%;
			letter-spacing: 0.02em;
			text-transform: unset;

			@media (max-width: 991.98px) {
				font-size: 2rem;
			}
		}

		h6 {
			font-weight: 300;
			font-size: 2rem;
			// line-height: 2.6rem;
			line-height: 130%;
			letter-spacing: 0.02em;
			text-transform: unset;
			margin-top: 0.9rem;

			@media (max-width: 991.98px) {
				font-size: 1.6rem;
			}
		}
	}

	// .case-detail__part
	&__part {
		&:not(:last-child) {
			margin-bottom: 2.8rem;

			@media (max-width: 991.98px) {
				margin-bottom: 1.4rem;
			}
		}
	}

	// .case-detail__list
	&__list {
		margin-top: 4.2rem;

		ul {
			display: flex;
			gap: 6rem;
		}
	}

	// .case-detail__action
	&__action {
		position: absolute;
		left: 2.5rem;
		z-index: 10;

		@media (min-width: 991.98px) {
			top: 75rem !important;
		}

		@media (max-width: 991.98px) {
			left: 0;
			top: 43rem;
			bottom: auto;
		}
	}

	// .case-detail__buttons
	&__buttons {
		display: flex;
		gap: 2rem;

		@media (max-width: 991.98px) {
			gap: 1rem;
		}
	}

	// .case-detail__button-prev
	&__button-prev {
	}

	// .case-detail__button-next
	&__button-next {
	}
}

.case {
	// .case__wrapper
	&__wrapper {
		overflow: visible;
		width: 100%;
		position: relative;
		margin: 5rem 0 0;
		display: block;
	}

	&__list {
		display: grid;
		grid-template-columns: repeat(4, 1fr);

		@media (max-width: 991.98px) {
			grid-template-columns: repeat(2, 1fr);
		}
	}

	// .case__item
	&__item {
		aspect-ratio: 1 / 1;
		transition:
			all 0.5s ease,
			opacity 0.5s ease,
			transform 0.5s ease;
		z-index: 1;
		overflow: hidden;
		position: relative;

		@media (max-width: 991.98px) {
			width: auto;
			height: auto;

			// &:nth-child(6n + 1) {
			// 	grid-column: 1/2;
			// 	grid-row: 1/2;
			// }
			// &:nth-child(6n + 2) {
			// 	grid-column: 2/3;
			// 	grid-row: 1/2;
			// }
			// &:nth-child(6n + 3) {
			// 	grid-column: 2/3;
			// 	grid-row: 2/3;
			// }
			// &:nth-child(6n + 4) {
			// 	grid-column: 1/3;
			// 	grid-row: 3/5;
			// }
			// &:nth-child(6n + 5) {
			// 	grid-column: 1/2;
			// 	grid-row: 5/6;
			// }
			// &:nth-child(6n + 6) {
			// 	grid-column: 2/3;
			// 	grid-row: 6/7;
			// }
		}

		&::before {
			content: '';
			position: absolute;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			z-index: 1;
			pointer-events: none;
			background-color: #fff;
			transition: all 0.3s ease 0s;
		}

		&:nth-child(6n + 1)::before {
			background: rgba(210, 45, 79, 0.8);
		}
		&:nth-child(6n + 2)::before {
			background: rgba(209, 23, 15, 0.8);
		}
		&:nth-child(6n + 3)::before {
			background: rgba(223, 158, 38, 0.8);
		}
		&:nth-child(6n + 4)::before {
			background: rgba(29, 55, 108, 0.8);
		}
		&:nth-child(6n + 5)::before {
			background: rgba(29, 55, 108, 0.8);
		}
		&:nth-child(6n + 6)::before {
			background: rgba(209, 23, 15, 0.8);
		}

		//========================================================================================================================================================
		@media (min-width: 991.98px) {
			@media (any-hover: hover) {
				&:hover {
					&::before {
						opacity: 0;
						visibility: hidden;
					}

					.case__desc {
						opacity: 0;
						visibility: hidden;
					}
				}
			}
		}
	}

	// .case__link
	&__link {
		display: block;
		width: 100%;
		height: 100%;
	}

	// .case__img
	&__img {
		width: 100%;
		height: 100%;

		img {
			width: 100%;
			height: 100%;
			object-fit: cover;
		}
	}

	// .case__desc
	&__desc {
		position: absolute;
		padding: 2rem 2.4rem;
		top: 0;
		left: 0;
		z-index: 2;
		transition: all 0.3s ease 0s;

		@media (max-width: 991.98px) {
			padding: 1.2rem 1.6rem;
		}

		h5 {
			margin-top: 0.8rem;
		}
	}
}
</style>
